import dayjs from "dayjs";

import { FR_CONTRACT_TYPE } from "constants/haulpay-broker";
import { routes } from "constants/routes";
import { getApiErrorMessage } from "helpers/apiError";
import { IOption } from "models/Form";
import { IPaymentSpeed } from "models/HaulPay";
import { IActivityItem } from "models/Home";
import { ILocations, IQueryLoadData, LoadSearchFilterFormData } from "models/Load";

export function getFormData(object: any) {
  const formData = new FormData();
  Object.keys(object).forEach((key) => formData.append(key, object[key]));
  return formData;
}

export function convertMsToRemainingTime(ms: number) {
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  return days > 0
    ? `${days} Days ${hours}:${minutes}:${seconds}`
    : `${hours}:${minutes}:${seconds}`;
}

export function convertDiffToAge(ms: number) {
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  if (days) return days + "d";
  if (hours) return hours + "h";
  if (minutes) return minutes + "m";
  if (seconds) return seconds + "s";
  return "-";
}

export const convertToFullAddress = (
  address: string,
  city: string,
  state: string,
  country: string
) => {
  const streetStr = [address, city, state, country]
    .filter((item) => !!item && item !== "undefined")
    .join(", ");
  return streetStr ?? "Anywhere";
};

export const convertTime = (time: string) => {
  return {
    formatted: dayjs(time).format("DD MMM YYYY"),
    day: dayjs(time).format("DD MMM"),
    time: dayjs(time).format("hh:mm A")
  };
};

export const convertTruckData = (data: any[]) => {
  return data.map((item: any, index) => ({
    ...item,
    timeStart: {
      timeStart: dayjs(item.timeStart).format("DD MMM YYYY"),
      startDay: dayjs(item.timeStart).format("DD MMM"),
      startTime: dayjs(item.timeStart).format("hh:mm A")
    },
    timeEnd: {
      timeEnd: dayjs(item.timeEnd).format("DD MMM YYYY"),
      endDay: dayjs(item.timeEnd).format("DD MMM"),
      endTime: dayjs(item.timeEnd).format("hh:mm A")
    },
    index
  }));
};

export const convertSavedLoadData = (data: any[]) => {
  return data.map(
    (item: any, index) =>
      ({
        ...item,
        timeStart: {
          timeStart: item.timeStart,
          startDay: dayjs(item.timeStart).format("DD MMM"),
          startTime: dayjs(item.timeStart).format("hh:mm A")
        },
        timeEnd: {
          timeEnd: item.timeEnd,
          endDay: dayjs(item.timeEnd).format("DD MMM"),
          endTime: dayjs(item.timeEnd).format("hh:mm A")
        },
        timeDelivery: {
          timeDelivery: item.timeDelivery ?? "",
          deliveryDay: item.timeDelivery ? dayjs(item.timeDelivery).format("DD MMM") : "",
          deliveryTime: item.timeDelivery ? dayjs(item.timeDelivery).format("hh:mm A") : ""
        },
        index
      }) as IQueryLoadData
  );
};

// Pull raw string/Date even if this was already converted once
const getRaw = (val: any, key: "timeStart" | "timeEnd" | "timeDelivery") => {
  if (val == null) return null;
  if (typeof val === "string" || val instanceof Date || typeof val === "number") return val;
  if (typeof val === "object") return val[key] ?? null; // already-converted case
  return null;
};

// Parse helper: try ISO first, then known formats (strict)
const parseDate = (input: any) => {
  if (input == null) return null;

  // 1) ISO (and plain Date/epoch) first
  let d = dayjs(input);
  if (d.isValid()) return d;

  // 2) Known custom formats (add more if you need)
  d = dayjs(input, ["DD MMM YYYY", "D MMM YYYY", "DD MMM YYYY hh:mm A"], true);
  if (d.isValid()) return d;

  // 3) Handle 6-digit fractional seconds like 2025-03-04T23:31:31.067380+00:00
  //    Day.js only supports milliseconds. Trim to 3 decimals and retry (optional).
  if (typeof input === "string" && /\.\d{6,}/.test(input)) {
    const trimmed = input.replace(/\.(\d{3})\d+/, ".$1");
    d = dayjs(trimmed);
    if (d.isValid()) return d;
  }

  return null;
};

export const convertLoadData = (data: any) => {
  const rawStart = getRaw(data.timeStart, "timeStart");
  const rawEnd = getRaw(data.timeEnd, "timeEnd");
  const rawDelivery = getRaw(data.timeDelivery, "timeDelivery");

  const dStart = parseDate(rawStart);
  const dEnd = parseDate(rawEnd);
  const dDelivery = parseDate(rawDelivery);

  return {
    ...data,
    timeStart: {
      timeStart: rawStart ?? "",
      startDay: dStart ? dStart.format("DD MMM") : "",
      startTime: dStart ? dStart.format("hh:mm A") : ""
    },
    timeEnd: {
      timeEnd: rawEnd ?? "",
      endDay: dEnd ? dEnd.format("DD MMM") : "",
      endTime: dEnd ? dEnd.format("hh:mm A") : ""
    },
    timeDelivery: {
      timeDelivery: rawDelivery ?? "",
      deliveryDay: dDelivery ? dDelivery.format("DD MMM") : "",
      deliveryTime: dDelivery ? dDelivery.format("hh:mm A") : ""
    }
  };
};

export const covertFrLocation = (location: ILocations) => {
  const { name, street_one, street_two, city, state, zip, country } = location;

  const locationName = name ? name + ":" : "";
  const locationAddress = [street_one, street_two, city, state, zip, country]
    .filter((item) => !!item && item !== undefined)
    .join(", ");

  return locationName + locationAddress;
};

export const convertLoadFilterFormData = (
  data: LoadSearchFilterFormData,
  tabValue: IOption,
  isDateRange: boolean
) => {
  const {
    originState,
    originRegion,
    originRadius,
    destinationState,
    destinationRegion,
    destinationRadius,
    equipmentType,
    weight,
    length,
    startDate,
    endDate
  } = data;

  let origin;

  if (originRegion?.length) {
    origin = originRegion[0]?.value;
  } else if (originState) {
    origin = originState?.state;
    origin = originState?.state;
  } else {
    origin = "";
  }

  let formData: any =
    tabValue.value === "by_city"
      ? {
          originCity: originState?.city,
          originState: origin,
          originRadius: Number(originRadius?.value),
          deliveryCity: destinationState?.city,
          destinationState: destinationState?.state,
          destinationRadius: Number(destinationRadius?.value),
          equipmentType: equipmentType?.length ? equipmentType.map(({ value }) => value) : [],
          length: length?.value,
          weight: weight
        }
      : {
          originState: originRegion?.length ? originRegion[0].value : "",
          originRadius: 200,
          destinationState: destinationRegion?.length ? destinationRegion[0].value : "",
          destinationRadius: 200,
          equipmentType: equipmentType?.length ? equipmentType.map(({ value }) => value) : [],
          length: length?.value,
          weight: weight
        };

  if (isDateRange) {
    formData = {
      ...formData,
      timeStart: startDate ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss") : null,
      timeEnd: endDate ? dayjs(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null
    };
  } else {
    formData = {
      ...formData,
      timeStart: startDate ? dayjs(startDate).format("YYYY-MM-DDTHH:mm:ss") : null,
      timeEnd: endDate ? dayjs(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null
    };
  }

  return formData;
};

export const getPath = (data: IActivityItem, account: any) => {
  if (data.debtor_id) {
    return routes.HAULPAY_CREDIT_CHECK_CREDIT;
  } else if (data.bid_id && data.load_id) {
    return `${routes.LOADS_SEARCH}/${data.load_id}`;
  } else if (data.load_id) {
    if (data.funding_request_id) {
      return routes.HAULPAY_ASSIGNED_LOADS_PAYMENT_DETAILS + "/" + data.funding_request_id;
    } else {
      return data.user_id === account.id
        ? `${routes.LOADS_POST}/${data.load_id}`
        : `${routes.LOADS_SEARCH}/${data.load_id}`;
    }
  } else if (data.truck_id) {
    return data.user_id === account.id
      ? `${routes.TRUCKS_SEARCH}/${data.truck_id}`
      : `${routes.TRUCKS_MY_POSTED}/${data.truck_id}`;
  } else if (data.factoring_id) {
    return (data.funding_request_id as any)
      ? `${routes.HAULPAY_CARRIER_MANGE_PAY_PAYMENT_DETAILS}/${data.funding_request_id}`
      : routes.HAULPAY_CREDIT_CHECK_CREDIT;
  } else if (
    data.alert_type === "factoring_application_pending" ||
    data.alert_type === "factoring_welcome"
  ) {
    return routes.HAULPAY_STATUS;
  }

  return "";
};

export const frPermission = (frType?: IOption) => {
  const isNonFactored =
    frType?.value !== FR_CONTRACT_TYPE[1].value && frType?.value !== FR_CONTRACT_TYPE[2].value;
  const isSelfFinance =
    frType?.value === FR_CONTRACT_TYPE[4].value || frType?.value === FR_CONTRACT_TYPE[5].value;

  return {
    isNonFactored,
    isSelfFinance
  };
};

export const getDefaultContractType = (
  availableContractTypes?: Record<string, boolean>,
  defaultFrTypeApi?: string | null
): IOption => {
  const availableTypes = FR_CONTRACT_TYPE.filter(
    (request) => !request.hidden && (availableContractTypes?.[request.value] ?? false)
  );
  return (
    availableTypes.find((type) => type.value === defaultFrTypeApi) ??
    availableTypes[0] ??
    FR_CONTRACT_TYPE[0]
  );
};

export const convertPaymentSpeedOption = (paymentSpeed: IPaymentSpeed): IOption => ({
  id: paymentSpeed?.payout_days,
  // label: `${paymentSpeed.in_process ? "Processing request for " : ""} ${paymentSpeed.name} (${paymentSpeed.carrier_rate}% Quick Pay Fee)`,
  label: `${paymentSpeed?.name} (${paymentSpeed?.carrier_rate}% Quick Pay Fee)`,
  value: `ach_${paymentSpeed?.payout_days === 0 ? 1 : paymentSpeed?.payout_days}_days`
});

/** @deprecated Use `getApiErrorMessage` / `normalizeApiError` from `helpers/apiError` instead. */
export const parseError = (error: string): string => getApiErrorMessage(error, error);
